import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Divider,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MovieIcon from "@mui/icons-material/Movie";
import CollectionsIcon from "@mui/icons-material/Collections";
import AssetLibraryModal from "../AssetLibraryModal";
import { Asset } from "../../context/types";
import { THEME_COLORS } from "../../context/constants";

interface MediaUploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File) => Promise<void>;
  onAssetSelect?: (asset: Asset) => void;
  type: "image" | "video";
}

const MediaUploadModal: React.FC<MediaUploadModalProps> = ({
  open,
  onClose,
  onUpload,
  onAssetSelect,
  type,
}) => {
  const [uploading, setUploading] = useState(false);
  const [assetLibraryOpen, setAssetLibraryOpen] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploading(true);
      try {
        await onUpload(e.target.files[0]);
        onClose();
      } catch (error) {
        console.error(`Error uploading ${type}:`, error);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleAssetSelect = (asset: Asset) => {
    if (onAssetSelect) {
      onAssetSelect(asset);
      setAssetLibraryOpen(false);
      onClose();
    }
  };

  const acceptTypes =
    type === "image" ? "image/jpeg,image/png,image/gif" : "video/mp4,video/quicktime";

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{`Upload or Select ${
          type.charAt(0).toUpperCase() + type.slice(1)
        }`}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
            {/* Upload from device */}
            <input
              accept={acceptTypes}
              style={{ display: "none" }}
              id={`${type}-upload-input`}
              type="file"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <label htmlFor={`${type}-upload-input`}>
              <Button
                variant="contained"
                component="span"
                fullWidth
                startIcon={
                  uploading ? (
                    <CircularProgress size={20} />
                  ) : type === "image" ? (
                    <FileUploadIcon />
                  ) : (
                    <MovieIcon />
                  )
                }
                disabled={uploading}
                sx={{ backgroundColor: "#FFBF00", color: "#000" }}
              >
                {uploading
                  ? "Uploading..."
                  : `Upload ${type.charAt(0).toUpperCase() + type.slice(1)} from Device`}
              </Button>
            </label>

            {/* Divider */}
            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color={THEME_COLORS.darkGray}>
                OR
              </Typography>
            </Divider>

            {/* Choose from library */}
            <Button
              variant="outlined"
              fullWidth
              startIcon={<CollectionsIcon />}
              onClick={() => setAssetLibraryOpen(true)}
              disabled={uploading}
              sx={{
                borderColor: THEME_COLORS.yellow,
                color: THEME_COLORS.darkYellow,
                "&:hover": {
                  borderColor: THEME_COLORS.darkYellow,
                  backgroundColor: "rgba(255, 191, 0, 0.1)",
                },
              }}
            >
              Choose from Asset Library
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asset Library Modal */}
      <AssetLibraryModal
        open={assetLibraryOpen}
        onClose={() => setAssetLibraryOpen(false)}
        mode="select"
        onSelect={handleAssetSelect}
        filterType={type}
        title={`Select ${type.charAt(0).toUpperCase() + type.slice(1)} from Library`}
      />
    </>
  );
};

export default MediaUploadModal;
